import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, MapPin, DollarSign, Calendar, Loader2, FileText, Home } from 'lucide-react';
import { fetchOfferDetails } from '../api/analyticsApi';

const CONDITION_LABELS = {
    SUBJECT_TO_FINANCE: 'Subject to Finance',
    SUBJECT_TO_BUILDING_INSPECTION: 'Subject to Building Inspection',
    SUBJECT_TO_PEST_INSPECTION: 'Subject to Pest Inspection',
    SUBJECT_TO_SALE: 'Subject to Sale of Property',
    UNCONDITIONAL: 'Unconditional',
};

const OfferDetailModal = ({ isOpen, onClose, startDate, endDate }) => {
    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) loadOffers();
    }, [isOpen, startDate, endDate]);

    const loadOffers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchOfferDetails(startDate, endDate);
            setOffers(Array.isArray(data) ? data : data?.results || []);
        } catch (err) {
            console.error('Error loading offers:', err);
            setError('Failed to load offers');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price) => {
        if (!price) return null;
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(price);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const getBuyerName = (offer) => {
        if (offer.buyer?.name) return offer.buyer.name;
        const first = offer.first_name || '';
        const last = offer.last_name || '';
        const full = `${first} ${last}`.trim();
        return full || null;
    };

    const getBuyerEmail = (offer) => offer.buyer?.email || offer.email || null;
    const getBuyerPhone = (offer) => offer.buyer?.phone || offer.mobile_number || null;

    const getConditionLabel = (cond) => {
        if (typeof cond === 'string') return CONDITION_LABELS[cond] || cond;
        const type = cond.type || '';
        const details = cond.details || '';
        return CONDITION_LABELS[type] || (details ? `${type}: ${details}` : type);
    };

    const statusStyle = (status) => {
        if (status === 'ACCEPTED') return 'bg-emerald-100 text-emerald-700';
        if (status === 'REJECTED') return 'bg-red-100 text-red-700';
        return 'bg-blue-100 text-blue-700';
    };

    const getPropertyAddress = (offer) =>
        offer.property?.address || offer.property?.title || offer.property_address || null;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Offers Received</h2>
                            <p className="text-xs text-gray-400">{offers.length} offer{offers.length !== 1 ? 's' : ''} in period</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-500 py-10">{error}</div>
                    ) : offers.length === 0 ? (
                        <div className="text-center text-gray-400 py-10">No offers found for this period</div>
                    ) : (
                        offers.map((offer, index) => {
                            const buyerName = getBuyerName(offer);
                            const buyerEmail = getBuyerEmail(offer);
                            const buyerPhone = getBuyerPhone(offer);
                            const propertyAddress = getPropertyAddress(offer);
                            const offerAmount = offer.offer_amount || offer.offer_amount_min;
                            const initial = buyerName ? buyerName[0].toUpperCase() : '?';

                            return (
                                <div key={offer.id || index} className="border border-gray-200 rounded-xl overflow-hidden">
                                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1.1fr]">

                                        {/* Left — Buyer */}
                                        <div className="p-5 space-y-4">
                                            {/* Name + status */}
                                            <div className="flex items-start gap-3">
                                                <div className="w-11 h-11 rounded-full bg-[#55C5D0] flex items-center justify-center text-white font-black text-base flex-shrink-0">
                                                    {initial}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <h3 className="font-bold text-gray-900 text-base leading-tight">
                                                            {buyerName || 'Unknown Buyer'}
                                                        </h3>
                                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusStyle(offer.status)}`}>
                                                            {offer.status_display || offer.status || 'Submitted'}
                                                        </span>
                                                    </div>
                                                    {offer.reference_number && (
                                                        <p className="text-xs font-semibold text-[#55C5D0] mt-0.5">{offer.reference_number}</p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Contact details */}
                                            <div className="space-y-1.5">
                                                {buyerEmail && (
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Mail className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                                        <a href={`mailto:${buyerEmail}`} className="text-blue-600 hover:underline truncate">{buyerEmail}</a>
                                                    </div>
                                                )}
                                                {buyerPhone && (
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Phone className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                                        <a href={`tel:${buyerPhone}`} className="text-blue-600 hover:underline">{buyerPhone}</a>
                                                    </div>
                                                )}
                                                {offer.created_at && (
                                                    <div className="flex items-center gap-2 text-xs text-gray-400">
                                                        <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                                                        {formatDate(offer.created_at)}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Message */}
                                            {offer.message && (
                                                <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                                                    <p className="text-[10px] font-bold text-amber-500 uppercase tracking-wider mb-1">Buyer Message</p>
                                                    <p className="text-sm text-gray-600 italic leading-relaxed">"{offer.message}"</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Right — Offer details */}
                                        <div className="bg-gray-50 border-l border-gray-100 p-5 space-y-4">
                                            {/* Amount */}
                                            <div className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm">
                                                <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider mb-1">Offer Amount</p>
                                                <p className="text-3xl font-black text-gray-900">
                                                    {offerAmount ? formatPrice(offerAmount) : <span className="text-gray-300 text-xl">No amount</span>}
                                                </p>
                                                {offer.deposit_amount > 0 && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Deposit: <span className="font-semibold text-gray-700">{formatPrice(offer.deposit_amount)}</span>
                                                    </p>
                                                )}
                                            </div>

                                            {/* Property + Settlement row */}
                                            <div className="grid grid-cols-1 gap-3">
                                                {propertyAddress && (
                                                    <div className="flex items-start gap-2">
                                                        <Home className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Property</p>
                                                            <p className="text-sm font-semibold text-gray-800 leading-tight">{propertyAddress}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex items-start gap-2">
                                                    <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Settlement</p>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            {offer.custom_settlement_date
                                                                ? formatDate(offer.custom_settlement_date)
                                                                : offer.settlement_days
                                                                    ? `${offer.settlement_days} days`
                                                                    : offer.preferred_settlement_period || 'Standard'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Conditions */}
                                            {offer.conditions && offer.conditions.length > 0 && (
                                                <div>
                                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Conditions</p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {offer.conditions.map((cond, i) => (
                                                            <span key={i} className="px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 shadow-sm">
                                                                {getConditionLabel(cond)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Agent notes */}
                                            {offer.agent_notes && (
                                                <div className="bg-white rounded-lg p-3 border border-[#55C5D0]/20">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <FileText className="w-3 h-3 text-[#55C5D0]" />
                                                        <p className="text-[10px] font-bold text-[#55C5D0] uppercase tracking-wider">Agent Notes</p>
                                                    </div>
                                                    <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{offer.agent_notes}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                    <p className="text-sm text-gray-400">{offers.length} offer{offers.length !== 1 ? 's' : ''} found</p>
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OfferDetailModal;
